import { difficultyOptions } from '@/MyFitness/Today/SearchFilterFieldsAndData';
import { debouncer, getDifficultyFromDiamond, parseStorage, saveStorage, useImperialUnits } from '@/Util/utils';

// Unlike the workouts dashboard -- which loads every workout up front and filters in the browser
// (see SearchFilterFieldsAndData) -- an athlete's activity history is unbounded, so /activities
// stays server-paginated. This class holds the same shape of filter state as its workout
// counterpart, but instead of running the filter itself it serialises to the query string
// ActivityController@getActivitiesData / filterActivities() already understand.

export type FilterOption<T = number> = { label: string; value: T };

/** A place chosen in the map picker: what the server filters on, plus what to call it on screen. */
export type PickedLocation = { lat: number; lng: number; label: string };

/** Which of the two location filters is being talked about. Both hold the same shape of value. */
export type LocationFilterKey = 'startsNear' | 'goesThrough';

/**
 * One athlete the coach or squad dashboard can be narrowed to. The page hands the whole roster in
 * as a prop -- it already knows exactly whose activities the endpoint will return -- so picking
 * athletes costs no extra request. Ids are club user ids; the server only ever intersects them with
 * the set the caller is already allowed to see.
 */
export type ActivityOwnerOption = {
  id: number;
  username: string;
  first_name?: string;
  last_name?: string;
  profile_photo?: string;
};

/** "First Last", falling back to the username for an athlete with no name set. */
export const ownerName = (owner: ActivityOwnerOption): string =>
  [owner.first_name, owner.last_name].filter(Boolean).join(' ').trim() || owner.username;

/**
 * Coordinates are stored to five decimals -- about a metre, far finer than the five-minute GPS
 * sampling the filter runs against, and short enough to keep the query string readable.
 */
const coord = (value: number): string => String(Math.round(value * 1e5) / 1e5);


/**
 * What a shadcn/bits-ui Select hands to onSelectedChange: its own `Selected<T>` (whose label is
 * optional), or undefined when the selection is cleared. Resolving it back to the option we
 * declared keeps the stored value canonical -- persist() and Select.Value both want a real label.
 */
type SelectedOption<T> = { value: T; label?: string };
const resolveOption = <T>(options: FilterOption<T>[], selected?: SelectedOption<T>): FilterOption<T> | null =>
  options.find((option) => option.value === selected?.value) ?? null;

// Values are the sortBy indexes filterActivities() switches on.
export const activitySortOptions: FilterOption[] = [
  { label: 'Date', value: 0 },
  { label: 'Name', value: 1 },
  { label: 'XSS', value: 2 },
  { label: 'Duration', value: 3 },
  { label: 'Difficulty', value: 4 },
  { label: 'Distance', value: 5 },
  { label: 'Elevation', value: 7 },
  { label: 'KGE', value: 8 },
];

export const activitySortDirOptions: FilterOption[] = [
  { label: 'Ascending', value: 0 },
  { label: 'Descending', value: 1 },
];

// The Workouts pill's selectedPill value -- the one activity-type filter that depends on tags.
export const WORKOUTS_ACTIVITY_TYPE = 4;

// Values are the selectedPill indexes filterActivities() switches on (see ReccTrainingPills).
export const activityTypeOptions: FilterOption[] = [
  { label: 'All', value: 0 },
  { label: 'Outdoor', value: 1 },
  { label: 'Virtual', value: 2 },
  { label: 'Sessions', value: 3 },
  { label: 'Workouts', value: WORKOUTS_ACTIVITY_TYPE },
];

// Values are matched against the activity's `fa` field, which stores Progression::ATHLETES keys.
export const activityFocusOptions: FilterOption<string>[] = [
  { label: 'All', value: 'all' },
  { label: 'Power Sprinter', value: 'Power Sprinter' },
  { label: 'Road Sprinter', value: 'Road Sprinter' },
  { label: 'Pursuiter', value: 'Pursuiter' },
  { label: 'Puncheur', value: 'Puncheur' },
  { label: 'Breakaway Specialist', value: 'Breakaway Specialist' },
  { label: 'Rouleur', value: 'Rouleur' },
  { label: 'GC Specialist', value: 'GC Specialist' },
  { label: 'Climber', value: 'Climber' },
  { label: 'Endurance', value: 'Endurance' },
];

// Values are matched against the activity's `spr` field.
export const activitySpecificityOptions: FilterOption<string>[] = [
  { label: 'All', value: 'all' },
  { label: 'Pure', value: 'Pure' },
  { label: 'Mixed', value: 'Mixed' },
  { label: 'Polar', value: 'Polar' },
];

/**
 * How wide a circle "starts near" and "goes through" draw around the place the athlete named.
 * Values are kilometres either way -- that's what the server filters on -- but they're labelled and
 * spaced in whichever units the athlete reads distances in. Nothing tighter than about a kilometre
 * is offered: GPS positions are only stored every five minutes, so a smaller circle would start
 * missing rides that genuinely went through a place (see ActivityPosition::createFromRecords).
 */
export const activityGpsRadiusOptions: FilterOption[] = useImperialUnits()
  ? [
      // { label: '1 mile', value: 1.6 },
      { label: '3 miles', value: 4.8 },
      { label: '10 miles', value: 16 },
      { label: '25 miles', value: 40 },
      { label: '50 miles', value: 80 },
    ]
  : [
      // { label: '1 km', value: 1 },
      { label: '5 km', value: 5 },
      { label: '10 km', value: 10 },
      { label: '25 km', value: 25 },
      { label: '50 km', value: 50 },
    ];

export { difficultyOptions };

// A diamond rating covers a band of difficulty values, so a "min ◆◆" filter has to compare against
// the bottom of the ◆◆ band (the top of the band below it) rather than the top -- otherwise
// selecting a minimum silently drops the activities that actually earned that rating.
const minDifficultyFor = (diamonds: number | null): number | null =>
  diamonds == null ? null : (getDifficultyFromDiamond(diamonds - 0.5) ?? 0);
const maxDifficultyFor = (diamonds: number | null): number | null =>
  diamonds == null ? null : (getDifficultyFromDiamond(diamonds) ?? null);

/** A `[min, max]` pair for one of the quick-pick buttons; null on either end means "no bound". */
export type FilterRange = [number | null, number | null];

/** Quick duration bands, in minutes -- the same values the two inputs beneath them take. */
export const activityDurationPresets: FilterOption<FilterRange>[] = [
  { label: '< 60 min', value: [null, 60] },
  { label: '60-90 min', value: [60, 90] },
  { label: '90+ min', value: [90, null] },
];

/**
 * The five names the app gives a difficulty (see getDifficultyType() in helpers.php), as the
 * diamond range each one covers. "Hard" is deliberately open-ended: pinning its top to the last
 * option would send maxDifficulty=200 and drop everything above it.
 */
export const activityDifficultyPresets: FilterOption<FilterRange>[] = [
  { label: 'Easy', value: [1, 1.5] },
  { label: 'Moderate', value: [2, 2.5] },
  { label: 'Difficult', value: [3, 3.5] },
  { label: 'Tough', value: [4, 4.5] },
  { label: 'Hard', value: [5, null] },
];

export const activityDateRangePresets: FilterOption<number | null>[] = [
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
  { label: 'Last year', value: 365 },
];

/** Local `YYYY-MM-DD`, which is what <input type="date"> reads and writes. */
const toDateInput = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const filterDebouncer = debouncer(400);

export class ActivityFilterFields {
  searchTerm: string = '';

  sortBy = activitySortOptions[0];
  sortDirection = activitySortDirOptions[1];

  minDuration: number | null = null;
  maxDuration: number | null = null;
  minXss: number | null = null;
  maxXss: number | null = null;
  minDifficulty = difficultyOptions[0];
  maxDifficulty = difficultyOptions[0];

  activityType = activityTypeOptions[0];
  selectedFocus = activityFocusOptions[0];
  selectedSpecificity = activitySpecificityOptions[0];

  // ISO `YYYY-MM-DD`, straight off an <input type="date">; the server widens each to the whole
  // local day. Empty string rather than null so the inputs can bind to them directly.
  dateFrom: string = '';
  dateTo: string = '';

  // "lat, lng" as chosen in the map picker, which is what filterActivitiesByLocation() reads --
  // it parses a coordinate pair directly, so the filter itself never costs a geocode. The paired
  // *Label is the name the picker found for that point, and exists only to be shown.
  startsNear: string = '';
  startsNearLabel: string = '';
  goesThrough: string = '';
  goesThroughLabel: string = '';
  gpsRadius = activityGpsRadiusOptions[1];

  // Athlete ids the coach/squad dashboard is narrowed to. Empty means everyone the page lists,
  // which is also what the personal dashboard always is -- it has no roster to choose from.
  members: number[] = [];

  // Tag names (standard or custom) to narrow the list to -- an activity matches if it has any one of
  // them. Empty means every activity, tagged or not. Named to match the `custom_tags` field
  // filterActivities() reads this from -- see Common/CustomActivityTags.svelte, which applies them.
  customTags: string[] = [];

  favouritesOnly: boolean = false;
  breakthroughsOnly: boolean = false;
  groupRidesOnly: boolean = false;
  // Widens the list rather than narrowing it: hidden activities are excluded by default, and this
  // adds them back alongside the visible ones.
  showHidden: boolean = false;

  // Favourites-only is forced on by /activities/favourites, where it isn't the athlete's choice
  // and so must neither be resettable nor count as an applied filter.
  readonly forceFavouritesOnly: boolean;
  // Hiding an activity only means anything on the athlete's own dashboard -- the coach and squad
  // endpoints never return other people's hidden rides -- so the toggle isn't offered there.
  readonly canShowHidden: boolean;

  protected onChange: () => void;
  protected onFieldChange: () => void;
  protected storageKey: string;

  constructor(
    onChange: () => void,
    onFieldChange: () => void,
    storageKey: string,
    forceFavouritesOnly = false,
    canShowHidden = true
  ) {
    this.onChange = onChange;
    this.onFieldChange = onFieldChange;
    this.storageKey = storageKey;
    this.forceFavouritesOnly = forceFavouritesOnly;
    this.canShowHidden = canShowHidden;
    this.restore();
  }

  /** Query string (without a leading `?`) for one page of the activities endpoint. */
  /**
   * `offset`, when given, asks for the activities after that many rather than for page `page` --
   * the activity dashboard's infinite scroll passes how many cards it already has.
   */
  toQuery(page: number, perPage: number, offset?: number): string {
    const params = new URLSearchParams();
    params.set('searchFavourites', String(this.forceFavouritesOnly || this.favouritesOnly));
    params.set('page', String(page));
    params.set('perPage', String(perPage));
    if (offset != null) params.set('offset', String(offset));
    params.set('sortBy', String(this.sortBy.value));
    params.set('sortOrder', String(this.sortDirection.value));

    if (this.searchTerm.trim()) params.set('search', this.searchTerm.trim());
    if (this.minDuration != null) params.set('minDuration', String(this.minDuration));
    if (this.maxDuration != null) params.set('maxDuration', String(this.maxDuration));
    if (this.minXss != null) params.set('minXSS', String(this.minXss));
    if (this.maxXss != null) params.set('maxXSS', String(this.maxXss));

    const minDifficulty = minDifficultyFor(this.minDifficulty.value);
    const maxDifficulty = maxDifficultyFor(this.maxDifficulty.value);
    if (minDifficulty != null) params.set('minDifficulty', String(minDifficulty));
    if (maxDifficulty != null) params.set('maxDifficulty', String(maxDifficulty));

    if (this.dateFrom) params.set('startDate', this.dateFrom);
    if (this.dateTo) params.set('endDate', this.dateTo);

    const startsNear = this.startsNear.trim();
    const goesThrough = this.goesThrough.trim();
    if (startsNear) params.set('startsNear', startsNear);
    if (goesThrough) params.set('goesThrough', goesThrough);
    // Only worth sending -- and only worth a geocode -- when there's a place for it to apply to.
    if (startsNear || goesThrough) params.set('gpsRadius', String(this.gpsRadius.value));

    if (this.members.length) params.set('members', this.members.join(','));
    // One customTags[] entry per tag rather than a joined string -- custom tag names may contain commas.
    for (const tag of this.customTags) params.append('customTags[]', tag);

    if (this.activityType.value !== 0) params.set('selectedPill', String(this.activityType.value));
    if (this.selectedFocus.value !== 'all') params.set('focus', this.selectedFocus.value);
    if (this.selectedSpecificity.value !== 'all') params.set('specificity', this.selectedSpecificity.value);
    if (this.breakthroughsOnly) params.set('showBrOnly', 'true');
    if (this.groupRidesOnly) params.set('groupRidesOnly', 'true');
    if (this.canShowHidden && this.showHidden) params.set('showHidden', 'true');

    return params.toString();
  }

  /** Quick date presets, as `dateFrom` values; `dateTo` always clears back to "up to now". */
  setDateRangePreset = (days: number | null) => {
    if (days == null) {
      this.dateFrom = '';
    } else {
      const from = new Date();
      from.setDate(from.getDate() - days);
      this.dateFrom = toDateInput(from);
    }
    this.dateTo = '';
    this.onFieldChange();
    this.apply();
  };

  /** Quick duration bands. Passing [null, null] is what the Clear button does. */
  setDurationPreset = (range: FilterRange) => {
    [this.minDuration, this.maxDuration] = range;
    this.onFieldChange();
    this.apply();
  };

  /** Quick difficulty bands, resolved back to the diamond options the two selects are bound to. */
  setDifficultyPreset = (range: FilterRange) => {
    const asOption = (value: number | null) =>
      difficultyOptions.find((option) => option.value === value) ?? difficultyOptions[0];
    this.minDifficulty = asOption(range[0]);
    this.maxDifficulty = asOption(range[1]);
    this.onFieldChange();
    this.apply();
  };

  executeFilterDebouncer = () => filterDebouncer.debounce(() => this.apply());

  /** Immediately persist + notify. Sorting and toggles have no reason to wait for a debounce. */
  apply = () => {
    this.persist();
    this.onChange();
  };

  onSortByChange = (selected?: SelectedOption<number>) => {
    const option = resolveOption(activitySortOptions, selected);
    if (!option) return;
    this.sortBy = option;
    this.onFieldChange();
    this.apply();
  };

  onSortDirectionChange = (selected?: SelectedOption<number>) => {
    const option = resolveOption(activitySortDirOptions, selected);
    if (!option) return;
    this.sortDirection = option;
    this.onFieldChange();
    this.apply();
  };

  /** The point a location filter is set to, in the shape the picker opens with. */
  locationPoint(which: LocationFilterKey): PickedLocation | null {
    const [lat, lng] = this[which].split(',').map((part) => Number(part.trim()));
    if (!isFinite(lat) || !isFinite(lng)) return null;
    return { lat, lng, label: this.locationLabel(which) };
  }

  locationLabel(which: LocationFilterKey): string {
    return which === 'startsNear' ? this.startsNearLabel : this.goesThroughLabel;
  }

  /**
   * Set (or, with null, clear) one of the location filters. The picker settles the radius at the
   * same time as the place, so it comes through here too rather than as a second change that would
   * reload the list all over again.
   */
  setLocation = (which: LocationFilterKey, place: PickedLocation | null, radiusKm?: number) => {
    if (radiusKm != null) {
      this.gpsRadius = resolveOption(activityGpsRadiusOptions, { value: radiusKm }) ?? this.gpsRadius;
    }
    if (which === 'startsNear') {
      this.startsNear = place ? `${coord(place.lat)}, ${coord(place.lng)}` : '';
      this.startsNearLabel = place?.label ?? '';
    } else {
      this.goesThrough = place ? `${coord(place.lat)}, ${coord(place.lng)}` : '';
      this.goesThroughLabel = place?.label ?? '';
    }
    this.onFieldChange();
    this.apply();
  };

  /** Add or remove one athlete from the member filter. */
  toggleMember = (id: number) => {
    this.members = this.members.includes(id)
      ? this.members.filter((member) => member !== id)
      : [...this.members, id];
    this.onFieldChange();
    this.apply();
  };

  clearMembers = () => {
    if (!this.members.length) return;
    this.members = [];
    this.onFieldChange();
    this.apply();
  };

  /** Add or remove one tag from the tag filter. */
  toggleCustomTag = (tag: string) => {
    this.customTags = this.customTags.includes(tag)
      ? this.customTags.filter((t) => t !== tag)
      : [...this.customTags, tag];
    this.onFieldChange();
    this.apply();
  };

  clearCustomTags = () => {
    if (!this.customTags.length) return;
    this.customTags = [];
    this.onFieldChange();
    this.apply();
  };

  /**
   * Drop remembered athletes who aren't on this page's roster. Filters are persisted under one key
   * per *kind* of dashboard, so every squad shares a key -- without this, opening a second squad
   * would carry the first one's selection across, and the server would intersect it away to an
   * empty list. Silent on purpose: nothing is reloaded, because this runs before the first load.
   */
  pruneMembers(available: number[]) {
    if (!this.members.length) return;
    const allowed = new Set(available);
    const kept = this.members.filter((id) => allowed.has(id));
    if (kept.length === this.members.length) return;
    this.members = kept;
    this.persist();
  }

  onGpsRadiusChange = (selected?: SelectedOption<number>) => {
    const option = resolveOption(activityGpsRadiusOptions, selected);
    if (!option) return;
    this.gpsRadius = option;
    this.onFieldChange();
    this.apply();
  };

  onNumericFilterChange = () => {
    const normalize = (v: any) => (v === '' || v == null || isNaN(Number(v)) ? null : Number(v));
    this.minDuration = normalize(this.minDuration);
    this.maxDuration = normalize(this.maxDuration);
    this.minXss = normalize(this.minXss);
    this.maxXss = normalize(this.maxXss);
    this.executeFilterDebouncer();
  };

  onBooleanOrCategoricalFilterChange = () => {
    // Switch/Checkbox fire on:click before the bound value settles, so let the DOM catch up first.
    setTimeout(() => this.apply(), 0);
  };

  resetFilters = () => {
    this.searchTerm = '';
    this.sortBy = activitySortOptions[0];
    this.sortDirection = activitySortDirOptions[1];
    this.minDuration = null;
    this.maxDuration = null;
    this.minXss = null;
    this.maxXss = null;
    this.minDifficulty = difficultyOptions[0];
    this.maxDifficulty = difficultyOptions[0];
    this.activityType = activityTypeOptions[0];
    this.selectedFocus = activityFocusOptions[0];
    this.selectedSpecificity = activitySpecificityOptions[0];
    this.dateFrom = '';
    this.dateTo = '';
    this.startsNear = '';
    this.startsNearLabel = '';
    this.goesThrough = '';
    this.goesThroughLabel = '';
    this.gpsRadius = activityGpsRadiusOptions[1];
    this.members = [];
    this.customTags = [];
    this.favouritesOnly = false;
    this.breakthroughsOnly = false;
    this.groupRidesOnly = false;
    this.showHidden = false;
    this.onFieldChange();
    this.apply();
  };

  isFilterApplied = (filterName: keyof ActivityFilterFields | null = null): boolean => {
    switch (filterName) {
      case 'searchTerm': return this.searchTerm.trim() !== '';
      case 'minDuration': return this.minDuration != null;
      case 'maxDuration': return this.maxDuration != null;
      case 'minXss': return this.minXss != null;
      case 'maxXss': return this.maxXss != null;
      case 'minDifficulty': return this.minDifficulty.value !== difficultyOptions[0].value;
      case 'maxDifficulty': return this.maxDifficulty.value !== difficultyOptions[0].value;
      case 'sortBy': return this.sortBy.value !== activitySortOptions[0].value;
      case 'sortDirection': return this.sortDirection.value !== activitySortDirOptions[1].value;
      case 'activityType': return this.activityType.value !== activityTypeOptions[0].value;
      case 'selectedFocus': return this.selectedFocus.value !== activityFocusOptions[0].value;
      case 'selectedSpecificity': return this.selectedSpecificity.value !== activitySpecificityOptions[0].value;
      case 'dateFrom': return this.dateFrom !== '';
      case 'dateTo': return this.dateTo !== '';
      case 'startsNear': return this.startsNear.trim() !== '';
      case 'goesThrough': return this.goesThrough.trim() !== '';
      // The radius only means anything alongside a place, so on its own it isn't a filter.
      case 'gpsRadius': return false;
      case 'members': return this.members.length > 0;
      case 'customTags': return this.customTags.length > 0;
      case 'favouritesOnly': return this.favouritesOnly;
      case 'breakthroughsOnly': return this.breakthroughsOnly;
      case 'groupRidesOnly': return this.groupRidesOnly;
      case 'showHidden': return this.showHidden;
      default:
        return (
          this.isFilterApplied('searchTerm') ||
          this.isFilterApplied('minDuration') || this.isFilterApplied('maxDuration') ||
          this.isFilterApplied('minXss') || this.isFilterApplied('maxXss') ||
          this.isFilterApplied('minDifficulty') || this.isFilterApplied('maxDifficulty') ||
          this.isFilterApplied('sortBy') || this.isFilterApplied('sortDirection') ||
          this.isFilterApplied('activityType') ||
          this.isFilterApplied('selectedFocus') || this.isFilterApplied('selectedSpecificity') ||
          this.isFilterApplied('dateFrom') || this.isFilterApplied('dateTo') ||
          this.isFilterApplied('startsNear') || this.isFilterApplied('goesThrough') ||
          this.isFilterApplied('members') || this.isFilterApplied('customTags') ||
          this.isFilterApplied('favouritesOnly') || this.isFilterApplied('breakthroughsOnly') ||
          this.isFilterApplied('groupRidesOnly') || this.isFilterApplied('showHidden')
        );
    }
  };

  // -- sessionStorage --------------------------------------------------------
  // Same key/session-scoped persistence the legacy dashboard used, so filters survive a click
  // through to an activity and back.

  protected persist() {
    const optionValue = <T>(o: FilterOption<T>) => o?.value ?? null;
    const state = {
      searchTerm: this.searchTerm,
      sortBy: optionValue(this.sortBy),
      sortDirection: optionValue(this.sortDirection),
      minDuration: this.minDuration,
      maxDuration: this.maxDuration,
      minXss: this.minXss,
      maxXss: this.maxXss,
      minDifficulty: optionValue(this.minDifficulty),
      maxDifficulty: optionValue(this.maxDifficulty),
      activityType: optionValue(this.activityType),
      selectedFocus: optionValue(this.selectedFocus),
      selectedSpecificity: optionValue(this.selectedSpecificity),
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
      startsNear: this.startsNear,
      startsNearLabel: this.startsNearLabel,
      goesThrough: this.goesThrough,
      goesThroughLabel: this.goesThroughLabel,
      gpsRadius: optionValue(this.gpsRadius),
      members: this.members,
      customTags: this.customTags,
      favouritesOnly: this.favouritesOnly,
      breakthroughsOnly: this.breakthroughsOnly,
      groupRidesOnly: this.groupRidesOnly,
      showHidden: this.showHidden,
    };
    saveStorage(this.storageKey, 'filters', state);
  }

  protected restore() {
    const state = parseStorage(this.storageKey)?.filters;
    if (!state) return;
    const find = <T>(options: FilterOption<T>[], value: T, fallback: FilterOption<T>) =>
      options.find((o) => o.value === value) ?? fallback;

    this.searchTerm = state.searchTerm ?? '';
    this.sortBy = find(activitySortOptions, state.sortBy, activitySortOptions[0]);
    this.sortDirection = find(activitySortDirOptions, state.sortDirection, activitySortDirOptions[1]);
    this.minDuration = state.minDuration ?? null;
    this.maxDuration = state.maxDuration ?? null;
    this.minXss = state.minXss ?? null;
    this.maxXss = state.maxXss ?? null;
    this.minDifficulty = find(difficultyOptions, state.minDifficulty ?? null, difficultyOptions[0]);
    this.maxDifficulty = find(difficultyOptions, state.maxDifficulty ?? null, difficultyOptions[0]);
    this.activityType = find(activityTypeOptions, state.activityType, activityTypeOptions[0]);
    this.selectedFocus = find(activityFocusOptions, state.selectedFocus, activityFocusOptions[0]);
    this.selectedSpecificity = find(activitySpecificityOptions, state.selectedSpecificity, activitySpecificityOptions[0]);
    this.dateFrom = state.dateFrom ?? '';
    this.dateTo = state.dateTo ?? '';
    this.startsNear = state.startsNear ?? '';
    this.startsNearLabel = state.startsNearLabel ?? '';
    this.goesThrough = state.goesThrough ?? '';
    this.goesThroughLabel = state.goesThroughLabel ?? '';
    this.gpsRadius = find(activityGpsRadiusOptions, state.gpsRadius, activityGpsRadiusOptions[1]);
    this.members = Array.isArray(state.members) ? state.members.filter((id: unknown) => typeof id === 'number') : [];
    this.customTags = Array.isArray(state.customTags)
      ? state.customTags.filter((tag: unknown) => typeof tag === 'string')
      : [];
    this.favouritesOnly = state.favouritesOnly ?? false;
    this.breakthroughsOnly = state.breakthroughsOnly ?? false;
    this.groupRidesOnly = state.groupRidesOnly ?? false;
    this.showHidden = this.canShowHidden && (state.showHidden ?? false);
  }
}
